import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Send } from "lucide-react";
import { toast } from "sonner";
import { subscribeNewsletter } from "../api";

const TikTokIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.5 3c.3 2 1.5 3.6 3.5 3.9v2.7c-1.3 0-2.5-.3-3.6-1v6.6c0 3.4-2.6 5.8-5.8 5.8-3.1 0-5.6-2.4-5.6-5.5 0-3.1 2.5-5.6 5.6-5.6.3 0 .6 0 .9.1v2.8c-.3-.1-.6-.2-.9-.2-1.5 0-2.7 1.2-2.7 2.8 0 1.6 1.2 2.8 2.7 2.8 1.6 0 2.9-1.2 2.9-3.2V3h2.6z" />
  </svg>
);

const socials = [
  { label: "Facebook", Icon: Facebook, url: "" },
  { label: "Instagram", Icon: Instagram, url: "" },
  { label: "TikTok", Icon: TikTokIcon, url: "" },
];

const Footer = () => {
  const [email, setEmail] = useState("");

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await subscribeNewsletter(email);
      toast.success("Welcome to the ritual", {
        description: "You're on the list — enjoy 10% off your first order."
      });
      setEmail("");
    } catch (err) {
      toast.error("Please enter a valid email");
    }
  };

  return (
    <footer className="bg-[#0b0a09] border-t border-[#2c2822] pt-20 pb-10">
      <div className="max-w-[1280px] mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          <div>
            <div className="flex flex-col leading-none mb-5">
              <span className="font-display text-[28px] text-gold-gradient font-semibold">Kanama</span>
              <span className="font-display text-[16px] text-[#f4efe4] tracking-[0.35em] uppercase -mt-1">
                Naturals
              </span>
            </div>
            <p className="text-[#a29a8c] text-sm leading-relaxed max-w-xs">
              Premium hair, body and beard care crafted with authentic African
              ingredients. Rooted in tradition, powered by nature.
            </p>
            <div className="mt-5 space-y-1.5 text-sm text-[#a29a8c]">
              <a href="mailto:admin@kanama.org" className="kn-link inline-block hover:text-[#c9a24b] transition-colors">admin@kanama.org</a>
              <p className="max-w-xs leading-relaxed">54 State Street, Ste 804 #15721, Albany, New York 12207, US</p>
            </div>
            <div className="flex gap-4 mt-6">
              {socials.filter((s) => s.url).map((s) => (
                <a
                  key={s.label}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="w-10 h-10 rounded-full border border-[#2c2822] flex items-center justify-center text-[#a29a8c] hover:border-[#c9a24b] hover:text-[#c9a24b] transition-colors"
                >
                  <s.Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-[#c9a24b] text-[13px] tracking-[0.2em] uppercase mb-5">Shop</h4>
            <ul className="space-y-3 text-sm text-[#a29a8c]">
              <li><Link to="/shop" className="kn-link hover:text-[#f4efe4]">All Products</Link></li>
              <li><Link to="/shop" className="kn-link hover:text-[#f4efe4]">Hair Care</Link></li>
              <li><Link to="/shop" className="kn-link hover:text-[#f4efe4]">Body Care</Link></li>
              <li><Link to="/shop" className="kn-link hover:text-[#f4efe4]">Beard & Grooming</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-[#c9a24b] text-[13px] tracking-[0.2em] uppercase mb-5">Company</h4>
            <ul className="space-y-3 text-sm text-[#a29a8c]">
              <li><Link to="/about" className="kn-link hover:text-[#f4efe4]">Our Story</Link></li>
              <li><Link to="/contact" className="kn-link hover:text-[#f4efe4]">Contact</Link></li>
              <li><a href="#" className="kn-link hover:text-[#f4efe4]">Shipping & Returns</a></li>
              <li><a href="#" className="kn-link hover:text-[#f4efe4]">FAQ</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-[#c9a24b] text-[13px] tracking-[0.2em] uppercase mb-5">The Ritual Letter</h4>
            <p className="text-[#a29a8c] text-sm mb-4">
              Join for early access and 10% off your first order.
            </p>
            <form onSubmit={subscribe} className="flex items-center border border-[#2c2822] rounded-full overflow-hidden focus-within:border-[#c9a24b] transition-colors">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                className="bg-transparent flex-1 px-4 py-3 text-sm text-[#f4efe4] placeholder:text-[#6b6459] outline-none"
              />
              <button type="submit" className="btn-gold px-4 py-3 flex items-center justify-center" aria-label="Subscribe">
                <Send size={16} />
              </button>
            </form>
          </div>
        </div>

        <div className="gold-hairline mb-8" />
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-[#6b6459] text-xs tracking-wide">
          <p>© {new Date().getFullYear()} Kanama Naturals. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="#" className="hover:text-[#c9a24b] transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-[#c9a24b] transition-colors">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
